import React from "react";
import styled from "styled-components";
import Button from "./Button";

// Styled Components
const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 101;
  background: #ffffff;
  color: #000000;
  padding: 1rem 2rem;
  display: grid;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 80px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr);

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    text-align: left;
    height: auto;
    padding: 1rem;
    position: relative;
    width: 100%;
  }
`;

const HeaderLeft = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "showBackButton"
})`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;

  @media (max-width: 768px) {
    flex: none;
    height: auto;
    order: 1;
    display: ${(props) => (props.showBackButton ? "flex" : "none")};
  }
`;

const HeaderCenter = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["hasRightActions", "hasBackButton"].includes(prop)
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  min-height: 1.75rem;
  overflow: hidden;
  min-width: 0;
  gap: 0.25rem;

  h1,
  h2 {
    font-size: 1.5rem;
    margin: 0;
    line-height: 1.2;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 1.75rem;

    svg {
      margin-top: 0.25rem;
    }

    @media (max-width: 768px) {
      font-size: 1.25rem;
      display: block;
      text-align: left;
    }
  }

  p {
    opacity: 0.5;
    font-size: 1rem;
    min-height: 1.5rem;
    line-height: 1.25;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;

    @media (max-width: 768px) {
      text-align: left;
    }
  }

  @media (max-width: 768px) {
    flex: 1;
    height: auto;
    order: 2;
    text-align: left;
    align-items: flex-start;
    max-width: calc(
      100% -
        ${(props) => {
          const leftSpace = props.hasBackButton ? "60px" : "0px";
          const rightSpace = props.hasRightActions ? "60px" : "0px";
          return `${leftSpace} - ${rightSpace}`;
        }}
    ); /* Account for back button and/or right actions */
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 768px) {
    flex: none;
    height: auto;
    justify-content: center;
    order: 3;
  }
`;

const StyledHeaderButton = styled(Button)`
  /* Override header variant styles for white background */
  background: #f8f9fa !important;
  color: #333 !important;
  border: 2px solid #e1e5e9 !important;
  padding: 0.5rem 0.75rem;

  &:hover:not(:disabled) {
    background: #e9ecef !important;
    border-color: #dee2e6 !important;
  }
`;

const HeaderSuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 0.125rem solid #c3e6cb;
  animation: slideInRight 0.3s ease-out;

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
`;

const HeaderErrorState = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 0.125rem solid #f5c6cb;
  animation: slideInRight 0.3s ease-out;

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
`;

function PageHeader({
  title,
  subtitle,
  showBackButton = false,
  onBack,
  onLogout,
  rightActions = [],
  successMessage,
  errorMessage
}) {
  const hasRightActions = !!(
    successMessage ||
    errorMessage ||
    rightActions.length > 0 ||
    onLogout
  );

  return (
    <HeaderWrapper>
      <HeaderLeft showBackButton={showBackButton}>
        {showBackButton && (
          <StyledHeaderButton variant="header" onClick={onBack}>
            ←
          </StyledHeaderButton>
        )}
      </HeaderLeft>

      <HeaderCenter
        hasRightActions={hasRightActions}
        hasBackButton={showBackButton}
      >
        {title && <h2>{title}</h2>}
        {subtitle && <p>{subtitle}</p>}
      </HeaderCenter>

      <HeaderRight>
        {successMessage && (
          <HeaderSuccessMessage>{successMessage}</HeaderSuccessMessage>
        )}
        {errorMessage && <HeaderErrorState>{errorMessage}</HeaderErrorState>}
        {rightActions.map((action, index) => (
          <StyledHeaderButton
            key={index}
            variant="header"
            onClick={action.onClick}
            style={action.style}
          >
            {action.label}
          </StyledHeaderButton>
        ))}
        {onLogout && (
          <StyledHeaderButton variant="header" onClick={onLogout}>
            Logout
          </StyledHeaderButton>
        )}
      </HeaderRight>
    </HeaderWrapper>
  );
}

export default PageHeader;
